/**
 * The guest menu: categories, dishes, and the item detail sheet.
 *
 * Everything visible here — category names, dish names, descriptions, option
 * group labels — arrives already translated from `GET /customer/menu`. Only the
 * chrome (buttons, headings) comes from the UI-string bundle. That split is
 * deliberate: dish names are business data that a human translator reviews,
 * button labels are product copy.
 */

import { useMemo, useState } from 'react';
import { EmptyState, ErrorState, LoadingState } from '@sonauto/ui';
import { useCustomerI18n } from './CustomerI18nProvider';
import { addGuestLine } from './cart';
import type { GuestCart } from './cart';
import type {
  CustomerMenuCategory,
  CustomerMenuModifier,
  CustomerMenuModifierGroup,
  CustomerMenuProduct,
  CustomerMenuResponse,
  CustomerMenuVariant,
} from '../lib/types';

export interface MenuScreenProps {
  readonly menu: CustomerMenuResponse | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly onRetry: () => void;
  readonly cart: GuestCart;
  readonly setCart: (cart: GuestCart) => void;
  readonly maxItemsPerOrder: number;
}

export function MenuScreen({ menu, loading, error, onRetry, cart, setCart }: MenuScreenProps): React.JSX.Element {
  const { tOr, price } = useCustomerI18n();
  const [openProduct, setOpenProduct] = useState<CustomerMenuProduct | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('');

  const categories = useMemo<CustomerMenuCategory[]>(() => {
    if (menu === null) return [];
    const list = [...menu.menu.categories];
    if (menu.menu.uncategorised.length > 0) {
      list.push({
        id: '__other',
        code: 'OTHER',
        name: tOr('customer.menu.category_all', 'Món khác'),
        description: null,
        sortOrder: 9999,
        translated: false,
        products: menu.menu.uncategorised,
      });
    }
    return list;
  }, [menu, tOr]);

  if (loading && menu === null) return <LoadingState label={tOr('customer.app.loading', 'Đang tải thực đơn…')} />;
  if (error !== null && menu === null) {
    return (
      <div className="p-4">
        <ErrorState
          title={tOr('customer.error.generic', 'Không tải được thực đơn')}
          message={error.message}
          onRetry={onRetry}
        />
      </div>
    );
  }
  if (menu === null) return <LoadingState />;
  if (categories.length === 0) {
    return <EmptyState title={tOr('customer.menu.empty', 'Thực đơn đang trống')} />;
  }

  const visible = activeCategory === '' ? categories : categories.filter((c) => c.id === activeCategory);

  return (
    <div>
      {/* Category rail. `whitespace-normal` + min-height: a long Russian
          category name wraps inside the chip instead of being cut off. */}
      <div className="sticky top-[73px] z-20 flex gap-2 overflow-x-auto border-b border-line bg-surface px-3 py-2">
        <CategoryChip
          active={activeCategory === ''}
          label={tOr('customer.menu.category_all', 'Tất cả')}
          onClick={() => setActiveCategory('')}
        />
        {categories.map((category) => (
          <CategoryChip
            key={category.id}
            active={activeCategory === category.id}
            label={category.name}
            onClick={() => setActiveCategory(category.id)}
          />
        ))}
      </div>

      {visible.map((category) => (
        <section key={category.id} className="px-3 py-4">
          <h2 className="text-flow text-lg font-bold text-fg">{category.name}</h2>
          {category.description !== null ? (
            <p className="text-flow mt-1 text-sm text-fg-muted">{category.description}</p>
          ) : null}
          {category.products.length === 0 ? (
            <p className="text-flow mt-2 text-sm text-fg-subtle">
              {tOr('customer.menu.category_empty', 'Nhóm này chưa có món.')}
            </p>
          ) : null}
          <ul className="mt-3 space-y-2">
            {category.products.map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  disabled={!product.isAvailable}
                  onClick={() => setOpenProduct(product)}
                  className="flex w-full items-start gap-3 rounded-lg bg-surface p-3 text-left shadow-card ring-1 ring-line disabled:opacity-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-flow text-base font-semibold text-fg">{product.name}</p>
                    {product.shortDescription !== null ? (
                      <p className="text-flow mt-0.5 text-sm text-fg-muted">{product.shortDescription}</p>
                    ) : null}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {product.allergens.length > 0 ? (
                        <Tag tone="danger">
                          {tOr('customer.item.allergens', 'Dị ứng')}: {product.allergens.map((a) => a.code).join(', ')}
                        </Tag>
                      ) : null}
                      {product.spiceLevel > 0 ? <Tag tone="warning">🌶 {product.spiceLevel}</Tag> : null}
                      {product.isVegetarian ? <Tag tone="success">🌱</Tag> : null}
                      {!product.isAvailable ? (
                        <Tag tone="neutral">{tOr('customer.menu.sold_out', 'Hết món')}</Tag>
                      ) : null}
                    </div>
                  </div>
                  <span className="shrink-0 text-base font-bold text-fg">{price(product.priceMinor)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {openProduct !== null ? (
        <ItemDetailSheet
          product={openProduct}
          onClose={() => setOpenProduct(null)}
          onAdd={(variant, modifiers, quantity, note, spice) => {
            setCart(addGuestLine(cart, openProduct, variant, modifiers, quantity, note, spice));
            setOpenProduct(null);
          }}
        />
      ) : null}
    </div>
  );
}

function CategoryChip({
  active,
  label,
  onClick,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-flow min-h-[40px] shrink-0 whitespace-normal rounded-full px-4 py-2 text-sm font-medium ${
        active ? 'bg-brand-600 text-white' : 'bg-surface-alt text-fg-muted'
      }`}
    >
      {label}
    </button>
  );
}

function Tag({ tone, children }: { readonly tone: string; readonly children: React.ReactNode }): React.JSX.Element {
  const classes: Record<string, string> = {
    danger: 'bg-danger-bg text-danger-fg',
    warning: 'bg-warning-bg text-warning-fg',
    success: 'bg-success-bg text-success-fg',
    neutral: 'bg-neutral-bg text-neutral-fg',
  };
  return (
    <span className={`text-flow rounded px-1.5 py-0.5 text-[11px] font-semibold ${classes[tone] ?? classes.neutral}`}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------

function ItemDetailSheet({
  product,
  onClose,
  onAdd,
}: {
  readonly product: CustomerMenuProduct;
  readonly onClose: () => void;
  readonly onAdd: (
    variant: CustomerMenuVariant | null,
    modifiers: readonly CustomerMenuModifier[],
    quantity: number,
    note: string | null,
    spiceLevel: number | null,
  ) => void;
}): React.JSX.Element {
  const { tOr, price } = useCustomerI18n();
  const [variantId, setVariantId] = useState<string | null>(product.variants.find((v) => v.isDefault)?.id ?? null);
  const [selected, setSelected] = useState<readonly string[]>(() =>
    product.modifierGroups.flatMap((g) => g.modifiers.filter((m) => m.isDefault).map((m) => m.id)),
  );
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [spice, setSpice] = useState<number | null>(null);

  const variant = product.variants.find((v) => v.id === variantId) ?? null;
  const chosen = product.modifierGroups.flatMap((g) => g.modifiers.filter((m) => selected.includes(m.id)));

  const problem = useMemo<string | null>(() => {
    for (const group of product.modifierGroups) {
      const count = group.modifiers.filter((m) => selected.includes(m.id)).length;
      if (group.isRequired && count < Math.max(1, group.minSelect)) {
        return tOr('customer.item.required_option', 'Vui lòng chọn tuỳ chọn bắt buộc: {name}', { name: group.name });
      }
      if (group.maxSelect > 0 && count > group.maxSelect) {
        return tOr('customer.item.select_max', 'Chọn tối đa {max}', { max: group.maxSelect });
      }
    }
    return null;
  }, [product.modifierGroups, selected, tOr]);

  const toggle = (group: CustomerMenuModifierGroup, modifierId: string): void => {
    const isOn = selected.includes(modifierId);
    if (isOn) {
      setSelected(selected.filter((id) => id !== modifierId));
      return;
    }
    if (group.selectionType === 'SINGLE' || group.maxSelect === 1) {
      const others = group.modifiers.map((m) => m.id);
      setSelected([...selected.filter((id) => !others.includes(id)), modifierId]);
      return;
    }
    setSelected([...selected, modifierId]);
  };

  const unit = variant === null ? product.priceMinor : variant.priceMinor;
  const total = (unit + chosen.reduce((acc, m) => acc + m.priceDeltaMinor, 0)) * quantity;

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-overlay/50" role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-surface pb-40">
        <div className="sticky top-0 flex items-start gap-3 border-b border-line bg-surface px-4 py-3">
          <h2 className="text-flow min-w-0 flex-1 text-lg font-bold">{product.name}</h2>
          <button type="button" onClick={onClose} aria-label={tOr('customer.nav.close', 'Đóng')} className="min-h-[44px] px-2 text-2xl">
            ×
          </button>
        </div>

        <div className="space-y-5 px-4 py-4">
          {product.description !== null ? (
            <p className="text-flow text-sm text-fg-muted">{product.description}</p>
          ) : null}

          {product.allergens.length > 0 ? (
            <section className="rounded-lg bg-danger-bg p-3">
              <h3 className="text-flow text-sm font-bold text-danger-fg">
                ⚠ {tOr('customer.item.allergen_warning', 'Cảnh báo dị ứng')}
              </h3>
              <p className="text-flow mt-1 text-sm text-danger-fg">
                {product.allergens
                  .map((a) => (a.confirmed ? a.code : `${a.code} (${tOr('customer.item.unavailable', 'chưa xác minh')})`))
                  .join(' · ')}
              </p>
            </section>
          ) : null}

          <dl className="grid grid-cols-2 gap-2 text-sm">
            {product.calories !== null ? (
              <div>
                <dt className="text-flow text-fg-subtle">{tOr('customer.item.calories', 'Calo')}</dt>
                <dd className="font-semibold">{product.calories}</dd>
              </div>
            ) : null}
            {product.spiceLevel > 0 ? (
              <div>
                <dt className="text-flow text-fg-subtle">{tOr('customer.option.spice_level', 'Độ cay')}</dt>
                <dd className="font-semibold">🌶 {product.spiceLevel}/5</dd>
              </div>
            ) : null}
          </dl>

          {product.variants.length > 0 ? (
            <section>
              <h3 className="text-flow text-sm font-bold">{tOr('customer.option.size', 'Lựa chọn')}</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <OptionButton key={v.id} active={variantId === v.id} onClick={() => setVariantId(v.id)}>
                    <span className="text-flow">{v.name}</span>
                    <span className="ml-2 text-xs">{price(v.priceMinor)}</span>
                  </OptionButton>
                ))}
              </div>
            </section>
          ) : null}

          {product.modifierGroups.map((group) => (
            <section key={group.id}>
              <h3 className="text-flow text-sm font-bold">
                {group.name}
                {group.isRequired ? <span className="ml-1 text-danger-fg">*</span> : null}
              </h3>
              <p className="text-flow text-xs text-fg-subtle">
                {group.isRequired
                  ? tOr('customer.item.required_option', 'Bắt buộc')
                  : tOr('customer.item.optional_option', 'Tuỳ chọn')}
                {group.maxSelect > 1 ? ` · ${tOr('customer.item.select_max', 'tối đa {max}', { max: group.maxSelect })}` : ''}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {group.modifiers.map((modifier) => (
                  <OptionButton
                    key={modifier.id}
                    active={selected.includes(modifier.id)}
                    onClick={() => toggle(group, modifier.id)}
                  >
                    <span className="text-flow">{modifier.name}</span>
                    {modifier.priceDeltaMinor !== 0 ? (
                      <span className="ml-2 text-xs">
                        {modifier.priceDeltaMinor > 0 ? '+' : ''}
                        {price(modifier.priceDeltaMinor)}
                      </span>
                    ) : (
                      <span className="ml-2 text-xs text-fg-subtle">{tOr('customer.option.free', 'miễn phí')}</span>
                    )}
                  </OptionButton>
                ))}
              </div>
            </section>
          ))}

          <section>
            <h3 className="text-flow text-sm font-bold">{tOr('customer.option.spice_level', 'Độ cay mong muốn')}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5].map((level) => (
                <OptionButton key={level} active={spice === level} onClick={() => setSpice(spice === level ? null : level)}>
                  {level === 0 ? tOr('customer.option.spice_none', 'Không cay') : `🌶 ${level}`}
                </OptionButton>
              ))}
            </div>
          </section>

          <section>
            <label className="text-flow block text-sm font-bold" htmlFor="item-note">
              {tOr('customer.note.label', 'Ghi chú')}
            </label>
            <textarea
              id="item-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={tOr('customer.note.placeholder', 'Ví dụ: không hành, ít đá…')}
              className="mt-1 w-full rounded-md bg-surface-alt p-3 text-base ring-1 ring-line"
            />
          </section>

          {problem !== null ? <p className="text-flow text-sm font-semibold text-danger-fg">{problem}</p> : null}
        </div>

        <div className="fixed inset-x-0 bottom-0 flex items-center gap-3 border-t border-line bg-surface p-3">
          <div className="flex items-center gap-2">
            <RoundButton onClick={() => setQuantity(Math.max(1, quantity - 1))} label={tOr('customer.quantity.decrease', 'Giảm')}>
              −
            </RoundButton>
            <span className="w-8 text-center text-lg font-bold tabular-nums">{quantity}</span>
            <RoundButton onClick={() => setQuantity(quantity + 1)} label={tOr('customer.quantity.increase', 'Tăng')}>
              +
            </RoundButton>
          </div>
          <button
            type="button"
            disabled={problem !== null || !product.isAvailable}
            onClick={() => onAdd(variant, chosen, quantity, note === '' ? null : note, spice)}
            className="text-flow min-h-[56px] flex-1 rounded-lg bg-brand-600 px-4 py-3 text-base font-bold text-white disabled:opacity-50"
          >
            {tOr('customer.cart.add_item', 'Thêm vào giỏ')} · {price(total)}
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionButton({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-flow inline-flex min-h-[48px] items-center rounded-lg px-3 py-2 text-sm ${
        active ? 'bg-brand-600 font-semibold text-white' : 'bg-surface-alt text-fg'
      }`}
    >
      {children}
    </button>
  );
}

function RoundButton({
  onClick,
  label,
  children,
}: {
  readonly onClick: () => void;
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="h-12 w-12 rounded-full bg-surface-alt text-2xl font-bold text-fg"
    >
      {children}
    </button>
  );
}
